import {
  Badge,
  HStack,
  Pressable,
  Text,
  VStack,
} from '@ayble-health/dashboard-components';
import { useToken } from '@chakra-ui/react';
import { Plus } from 'lucide-react';
import LaneBlockCard from './LaneBlockCard';
import { useUserBlocks } from 'api/healthCoach/useUserBlocks';
import {
  BLOCK_STABLE_STRINGS,
  BLOCK_TYPE,
  BlockArchetype,
  BlockType,
  UserBlock,
} from 'models/block';
import { UserBlockAction } from 'components/UserDashboard/overview/types';
import { Loader } from 'components/Loader';

type Props = {
  title: string;
  blockType: BlockType;
  archived?: boolean;
  openEditBlockDrawer: (block: UserBlock) => void;
  openCreateBlockDrawer: (blockType?: BlockType) => void;
  openNutritionBlockDetailsDrawer: (block: UserBlock) => void;
  handleUserBlockAction: (
    action: UserBlockAction,
    userBlockId?: string
  ) => void;
};

export const BlockLane = ({
  title,
  blockType,
  archived,
  openEditBlockDrawer,
  openCreateBlockDrawer,
  openNutritionBlockDetailsDrawer,
  handleUserBlockAction,
}: Props) => {
  const [secondary200] = useToken('colors', ['secondary.200']);
  const addableBlockType = blockType !== BLOCK_TYPE.Clinical;

  const {
    blocks: rawUserBlocks,
    isLoading: blocksLoading,
    isError,
  } = useUserBlocks({
    blockType,
    archived,
  });

  // Surfaced separately in the Patient Summary card, not as a lane card.
  const userBlocks = rawUserBlocks.filter(
    (block) => block.stable_string !== BLOCK_STABLE_STRINGS.uncoveredGoals
  );

  const {
    blocks: contraIndicatedBlocks,
    isLoading: contraindicatedBlocksLoading,
  } = useUserBlocks({
    blockType,
    archived: false,
    archetype: BlockArchetype.Contraindicated,
    enable: addableBlockType,
  });

  const isLoading =
    blocksLoading || (contraindicatedBlocksLoading && addableBlockType);

  // If active contraindicated block, restrict assigning other blocks
  const contraindicatedBlockRestriction = contraIndicatedBlocks?.length;
  const canAddBlocks = addableBlockType && !contraindicatedBlockRestriction;

  return (
    <VStack
      bgColor={'feather.100'}
      borderRadius={'2xl'}
      flex={1}
      minW={'250px'}
      overflow={'hidden'}
      p={3}
    >
      <HStack
        alignItems={'center'}
        justifyContent={'space-between'}
        px={2}
        py={1}
      >
        <HStack alignItems={'center'} gap={2}>
          <Text
            color={'secondary.500'}
            fontSize={'16px'}
            fontFamily={'CircularXX'}
            fontStyle={'normal'}
            fontWeight={'500'}
            textAlign={'left'}
          >
            {title}
          </Text>

          <Badge
            bgColor={'secondary.100'}
            rounded={'md'}
            px={2}
            py={1}
            color={'white'} fontSize={'xs'} fontWeight={'bold'}
          >
            {userBlocks?.length}
          </Badge>
        </HStack>

        <Pressable
          rounded={'full'}
          p={2}
          mr={-4}
          _disabled={{ opacity: 0.4 }}
          _hover={{ bg: 'primary.50' }}
          cursor={!canAddBlocks ? 'not-allowed' : 'pointer'}
          disabled={!canAddBlocks || isLoading}
          onClick={() => {
            openCreateBlockDrawer(blockType);
          }}
        >
          <Plus color={secondary200} size={16} strokeWidth={2.5} />
        </Pressable>
      </HStack>

      {isError ? (
        <VStack mt={3}>
          <Text px={2} fontSize={'sm'} fontStyle={'italic'} color={'error.600'}>
            Unable to load blocks
          </Text>
        </VStack>
      ) : isLoading ? (
        <Loader loading />
      ) : (
        <VStack pt={3} gap={3}>
          {!userBlocks?.length && (
            <Text
              color={'blueGray.600'}
              fontSize={'sm'}
              px={2}
              fontStyle={'italic'}
            >
              No blocks.
            </Text>
          )}

          {userBlocks?.map((block) => {
            return (
              <LaneBlockCard
                enableCompletedBanner={!archived}
                handleArchive={() => handleUserBlockAction('archive', block.id)}
                key={block.id}
                block={block}
                openEditBlockDrawer={openEditBlockDrawer}
                openNutritionBlockDetailsDrawer={
                  openNutritionBlockDetailsDrawer
                }
              />
            );
          })}
        </VStack>
      )}
    </VStack>
  );
};

export default BlockLane;
